/**
 * AdminPeerHandler - Implements PeerTypeHandler for admin session peers.
 *
 * Replaces the legacy AdminSessionManager. Manages admin session lifecycle:
 * input filter toggling, elevated PowerShell launch, idle timer with
 * warning/kick, session end notification, and reboot scheduling.
 * Chunked elevated PowerShell script delivery is delegated to AdminPowerShellScriptManager.
 *
 * Registered with PeerTypeRegistry for the 'admin' peer type.
 * Called by InputManager for admin control messages (adminSessionExit).
 * Called by realtime WebSocket handler for ADMIN_SESSION_END messages.
 */

import * as Sentry from '@sentry/react';
import type { PeerMetadata } from '@playcastdotio/Messaging';
import { generators, websocket, AdminMessageType, type AdminSessionExitAction } from '@playcastdotio/Messaging';
import type { PeerTypeHandler, SerializableMessage } from '../types';
import type { IIpcService } from '../../../services/ipc/IIpcService';
import type { IRealtimeService } from '../../../services/realtime/IRealtimeService';
import type { AppCoordinator } from '../../app/AppCoordinator';
import type { PeerCoordinator } from '../PeerCoordinator';
import type { PavilionCoordinator } from '../../pavilion/PavilionCoordinator';
import {
  hostInputDominanceState,
  hostMatchmakingState,
  machineIdState,
} from '../../../api/stores/shared'; // Accepted exception per user guidance
import { setSuppressHostDominanceKick } from '../../../api/modules/events/adminConnectGuard';
import { AdminPowerShellScriptManager } from '../managers/AdminPowerShellScriptManager';
import type { RecordingControlManager } from '../../../services/playjector/managers/RecordingControlManager';

export type AdminSessionDisconnectReason = 'admin_exit' | 'timeout' | 'unexpected';

export type AdminPeerHandlerConfig = {
  idleTimeout: number;
  idleWarning: number;
};

const DEFAULT_CONFIG: AdminPeerHandlerConfig = {
  idleTimeout: 300_000,
  idleWarning: 240_000,
};

const ADMIN_SESSION_EXIT_ACTIONS: AdminSessionExitAction[] = [
  'reboot',
  'restart_game',
  'keep_game',
  'exit_nonsole',
];

/** Actions that carry a takeId we can validate before relaying to Playjector. */
const TAKEID_BEARING_ACTIONS = new Set(['macroRecorderScreenshot', 'macroRecorderRecordControl']);
const MACRO_TAKEID_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function isMacroUuid(value: unknown): value is string {
  return typeof value === 'string' && MACRO_TAKEID_UUID_RE.test(value);
}

function parseAdminSessionExitAction(
  msg: { exitAction?: unknown; reboot?: unknown },
  fallback: AdminSessionExitAction = 'exit_nonsole',
): AdminSessionExitAction {
  if (
    typeof msg.exitAction === 'string'
    && ADMIN_SESSION_EXIT_ACTIONS.includes(msg.exitAction as AdminSessionExitAction)
  ) {
    return msg.exitAction as AdminSessionExitAction;
  }
  if (msg.reboot === true) {
    return 'reboot';
  }
  return fallback;
}

export interface ISessionPresenceUpdater {
  setAdminSessionActive(active: boolean): void;
  removeHostingStatus(): void;
}

export type AdminPeerHandlerDeps = {
  ipcService: IIpcService;
  realtimeService: IRealtimeService;
  appCoordinator: AppCoordinator;
  peerCoordinator: PeerCoordinator;
  pavilionCoordinator: PavilionCoordinator;
  sessionPresenceUpdater: ISessionPresenceUpdater;
  recordingControlManager?: RecordingControlManager;
};

export class AdminPeerHandler implements PeerTypeHandler {
  private isAdminSession = false;
  private adminPeerId: string | null = null;
  private adminDisconnectReason: AdminSessionDisconnectReason = 'unexpected';
  private adminRebootRequested = false;
  private adminExitAction: AdminSessionExitAction = 'exit_nonsole';
  /**
   * Macro-recorder sessions use a non-destructive disconnect default
   * ('restart_game') so an abrupt drop/reload restarts the game but keeps the
   * host in Nonsole/guest mode. Set from peer metadata at connect, with a
   * message-based fallback (see handleMacroRecorderMessage).
   */
  private isMacroRecorderSession = false;
  private idleWarningTimer: ReturnType<typeof setTimeout> | null = null;
  private idleKickTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly psScriptManager: AdminPowerShellScriptManager;

  constructor(
    private readonly deps: AdminPeerHandlerDeps,
    private readonly config: AdminPeerHandlerConfig = DEFAULT_CONFIG,
  ) {
    this.psScriptManager = new AdminPowerShellScriptManager({
      ipcService: deps.ipcService,
      appCoordinator: deps.appCoordinator,
      notifyActivity: () => this.resetIdleTimer(),
    });
  }

  // ========================================
  // PeerTypeHandler interface
  // ========================================

  public onConnected(peerId: string, metadata: PeerMetadata): void {
    console.log('[AdminPeerHandler] Admin peer connected:', peerId);

    this.isAdminSession = true;
    this.adminPeerId = peerId;
    this.adminDisconnectReason = 'unexpected';
    this.adminRebootRequested = false;
    // Macro-recorder sessions default to a non-destructive exit so an abrupt
    // drop/reload does not boot the machine out of Nonsole/guest mode.
    this.isMacroRecorderSession = metadata?.purpose === 'macroRecorder';
    this.adminExitAction = this.isMacroRecorderSession ? 'restart_game' : 'exit_nonsole';
    if (this.isMacroRecorderSession) {
      console.log('[AdminPeerHandler] Macro-recorder session — abrupt disconnect will restart game, retain Nonsole Mode');
    }

    const hostId = this.deps.appCoordinator.getHostAuthId();

    // PeerCoordinator already armed adminSession (toggleFilters) and bound Cam03 before
    // the SDP answer. Resync filters only (idempotent) — a second getSources here would
    // race the cold-bind / gUM handshake.
    this.deps.ipcService.send(
      generators.input.toggleFilters(hostId, { keyboard: false, mouse: false, gamepad: false }),
    );
    console.log('[AdminPeerHandler] Input filtering resynced for admin session (arm owned by PeerCoordinator)');

    // Suppress HOST_DOMINANCE TS kick only while the admin peer is connected.
    setSuppressHostDominanceKick(true);

    this.deps.pavilionCoordinator.notifyAdminSessionChanged(true);
    console.log('[AdminPeerHandler] Matchmaking recalculated for admin session start');

    this.deps.sessionPresenceUpdater.setAdminSessionActive(true);

    this.startIdleTimer();
  }

  public onDisconnected(peerId: string): void {
    if (!this.isAdminSession || this.adminPeerId !== peerId) return;

    console.log('[AdminPeerHandler] Admin peer disconnected:', peerId, 'reason:', this.adminDisconnectReason);

    const hostId = this.deps.appCoordinator.getHostAuthId();
    const exitAction = this.adminExitAction;
    const exitGuestMode = exitAction === 'reboot' || exitAction === 'exit_nonsole';
    const shouldReboot = exitAction === 'reboot';

    this.deps.ipcService.send(
      generators.input.toggleFilters(hostId, {
        keyboard: true,
        mouse: true,
        gamepad: true,
        exitGuestModeAfterAdminSession: exitGuestMode,
        rebootIfSafeAfterGuestModeExit: shouldReboot,
      }),
    );

    // Clear stale dominance so the next physical key/mouse edge triggers host hard-kick again (Playjector
    // reset alone did not always sync host UI state before InputThread.cpp fix; this covers older builds too).
    hostInputDominanceState.updateState({ keyboard: false, mouse: false });
    setSuppressHostDominanceKick(false);

    const guestAccount = hostMatchmakingState.currentState.guestAccount;
    if (guestAccount) {
      this.deps.ipcService.send(
        generators.process.processSetPolicy(
          hostId,
          true,
          false,
          true,
          true,
          60000,
          true,
          true,
          true,
          true,
          true,
        ),
      );
    }

    this.clearIdleTimer();

    if (exitAction === 'restart_game') {
      void this.deps.pavilionCoordinator.restartGameForAdminExit().catch((err) => {
        console.error('[AdminPeerHandler] restartGameForAdminExit failed:', err);
      });
    } else if (exitGuestMode) {
      // Destructive exits (reboot / exit Nonsole) tear the machine down. Force
      // Pavilion into a terminal non-matchmakeable state so it cannot re-enter
      // the pool in the window before PlaycastService completes the guest-mode
      // exit / reboot. This also stops the broadcast (removes hosting status).
      this.deps.pavilionCoordinator.enterTerminalExitState(exitAction);
    } else {
      // keep_game: stop the current stream but let the host re-enter matchmaking
      // so the next player can connect to the still-running game.
      this.deps.sessionPresenceUpdater.removeHostingStatus();
    }

    const machineId = machineIdState.currentState;
    this.deps.realtimeService.sendMessage(
      websocket.generators.admin(AdminMessageType.ADMIN_SESSION_END, {
        sessionId: peerId,
        machineId,
        reason: this.adminDisconnectReason,
      }),
    );

    this.isAdminSession = false;
    this.adminPeerId = null;
    this.adminDisconnectReason = 'unexpected';
    this.adminRebootRequested = false;
    this.adminExitAction = 'exit_nonsole';
    this.psScriptManager.clearTransfers();

    this.deps.pavilionCoordinator.notifyAdminSessionChanged(false);
    this.deps.sessionPresenceUpdater.setAdminSessionActive(false);
    console.log('[AdminPeerHandler] Matchmaking recalculated after admin session end');

    if (shouldReboot) {
      console.log(
        '[AdminPeerHandler] Reboot-after-admin requested via toggleFilters (PlaycastService exits guest mode first, then reboots if no user sessions remain)',
      );
    } else if (exitAction === 'restart_game') {
      console.log('[AdminPeerHandler] Admin exit restart_game — game relaunch requested, Nonsole Mode retained');
    } else if (exitAction === 'keep_game') {
      console.log('[AdminPeerHandler] Admin exit keep_game — game and Nonsole Mode retained');
    }
  }

  public onMessage(peerId: string, _message: SerializableMessage): void {
    if (this.adminPeerId === peerId) {
      this.resetIdleTimer();
    }
  }

  // ========================================
  // Admin control messages (from data channel via InputManager)
  // ========================================

  public handleControlMessage(peerId: string, body: unknown, headerAction?: string): void {
    if (!this.isAdminSession || this.adminPeerId !== peerId) return;

    const msg = body as Record<string, unknown> | undefined;
    if (!msg) {
      Sentry.logger.warn('[AdminPeerHandler] Received admin control message with no body');
      return;
    }

    if (msg.adminSessionExit === true) {
      console.log('[AdminPeerHandler] Received adminSessionExit from admin peer');
      this.adminDisconnectReason = 'admin_exit';
      this.adminExitAction = parseAdminSessionExitAction(msg, this.adminExitAction);
      this.adminRebootRequested = this.adminExitAction === 'reboot';
      if (this.adminRebootRequested) {
        console.log('[AdminPeerHandler] Reboot requested with admin exit');
      }
      return;
    }

    if (msg.powerShellScriptChunk === true) {
      this.psScriptManager.handleScriptChunk(msg);
      return;
    }

    if (msg.launchElevatedProcess === true) {
      this.handleLaunchElevatedProcess(msg);
      return;
    }

    // macroRecorder* messages — route to RecordingControlManager
    if (this.isMacroRecorderMessage(msg, headerAction)) {
      this.handleMacroRecorderMessage(peerId, msg, headerAction);
      return;
    }

    this.resetIdleTimer();
  }

  // ========================================
  // Macro recorder message routing
  // ========================================

  private static readonly MACRO_RECORDER_ACTIONS = new Set([
    'macroRecorderScreenshot',
    'macroRecorderRecordControl',
    'macroRecorderUploadScreenshots',
    'macroRecorderKeepalive',
  ]);

  private isMacroRecorderMessage(msg: Record<string, unknown> | undefined, headerAction?: string): boolean {
    // If the header action is present and matches a macroRecorder action, treat it as a macroRecorder message
    if (headerAction && AdminPeerHandler.MACRO_RECORDER_ACTIONS.has(headerAction)) 
      return true;

    if (!msg) 
      return false;

    const action = msg.action as string | undefined;

    return !!action && AdminPeerHandler.MACRO_RECORDER_ACTIONS.has(action);
  }

  private handleMacroRecorderMessage(peerId: string, msg: Record<string, unknown>, headerAction?: string): void {
    this.resetIdleTimer();

    // Safety net: if peer metadata did not carry the macro-recorder purpose at
    // connect, receiving any macroRecorder* message proves this is a recorder
    // session — upgrade the still-default exit action so an abrupt disconnect
    // stays non-destructive.
    if (!this.isMacroRecorderSession && this.adminExitAction === 'exit_nonsole') {
      this.isMacroRecorderSession = true;
      this.adminExitAction = 'restart_game';
      console.log('[AdminPeerHandler] Detected macro-recorder session via control message — abrupt disconnect will retain Nonsole Mode');
    }

    const action = headerAction || (msg.action as string);

    if (action === 'macroRecorderKeepalive') {
      Sentry.logger.info('[AdminPeerHandler] macro-recorder macroRecorderKeepalive received', {
        feature: 'macro-recorder', transport: 'webrtc-dc', direction: 'receive', action,
      });
      return;
    }

    const manager = this.deps.recordingControlManager;
    if (!manager) {
      console.warn('[AdminPeerHandler] macroRecorder message received but no RecordingControlManager configured');
      Sentry.captureMessage('[MACRO-SCREENSHOT] macroRecorder message dropped — no RecordingControlManager', {
        level: 'error',
        extra: { action },
      });
      return;
    }

    const message = msg as Record<string, unknown> | undefined;
    if (!message) {
      console.warn('[AdminPeerHandler] macroRecorder message missing message body');
      Sentry.captureMessage('[MACRO-SCREENSHOT] macroRecorder message missing message body', {
        level: 'warning',
        extra: { action },
      });
      return;
    }

    // Defensive unwrap: InputManager forwards the already-inner payload
    // (body.message), but tolerate a doubly-wrapped { message: {...} } so a
    // mis-nested envelope never strands the takeId (which would look "empty" to
    // the guards below and to Playjector).
    const payload = (message.message as Record<string, unknown> | undefined) ?? message;
    const requestId = payload.requestId as string | undefined;
    const takeId = payload.takeId as string | undefined;
    const receiveTs = Date.now();

    Sentry.logger.info('[AdminPeerHandler] macro-recorder DC receive macroRecorder action', {
      feature: 'macro-recorder', transport: 'webrtc-dc', direction: 'receive',
      action, requestId, takeId,
    });

    Sentry.addBreadcrumb({
      category: 'MACRO-SCREENSHOT',
      level: 'info',
      message: 'inbound macroRecorder message',
      data: { action, requestId, takeId },
    });

    // High-signal instrumentation: the dashboard is fully guarded, so a missing /
    // non-UUID takeId arriving here means the value is entering from an
    // un-instrumented sender (stale build, alternate emitter) or is being lost in
    // transit. Capture the raw inbound payload once at the boundary so the next
    // run reveals the true origin. (Header source is not plumbed to this handler.)
    if (TAKEID_BEARING_ACTIONS.has(action) && !isMacroUuid(payload.takeId)) {
      Sentry.captureMessage('[MACRO-DEBUG] macroRecorder message with bad takeId', {
        level: 'warning',
        extra: {
          action,
          peerId,
          requestId: payload.requestId,
          takeId: payload.takeId,
          rawMessage: JSON.stringify(message).slice(0, 2000),
        },
      });
    }

    if (action === 'macroRecorderScreenshot') {
      void manager
        .handleMacroRecorderScreenshot(payload as any)
        .then((reply) => {
          if (!reply.success) {
            Sentry.captureMessage('[MACRO-SCREENSHOT] screenshot reply success=false', {
              level: 'warning',
              extra: { requestId, error: reply.error, takeId },
            });
          }
          this.sendDataChannelReply(peerId, 'macroRecorderScreenshot', reply);
          Sentry.logger.info('[AdminPeerHandler] macro-recorder DC reply sent macroRecorderScreenshot', {
            feature: 'macro-recorder', transport: 'webrtc-dc', direction: 'send',
            action, requestId, takeId, latencyMs: Date.now() - receiveTs,
          });
        })
        .catch((err) => {
          Sentry.captureMessage('[MACRO-SCREENSHOT] handleMacroRecorderScreenshot threw', {
            level: 'error',
            extra: {
              requestId,
              takeId,
              error: err instanceof Error ? err.message : String(err),
            },
          });
          this.sendDataChannelReply(peerId, 'macroRecorderScreenshot', {
            requestId,
            success: false,
            error: err instanceof Error ? err.message : String(err),
          });
        });
    } else if (action === 'macroRecorderRecordControl') {
      void manager.handleRecordControl(payload as any).then((reply) => {
        this.sendDataChannelReply(peerId, 'macroRecorderRecordControl', reply);
        Sentry.logger.info('[AdminPeerHandler] macro-recorder DC reply sent macroRecorderRecordControl', {
          feature: 'macro-recorder', transport: 'webrtc-dc', direction: 'send',
          action, requestId, takeId, latencyMs: Date.now() - receiveTs,
        });
      }).catch((err) => {
        Sentry.captureMessage('[MACRO-SCREENSHOT] handleRecordControl threw', {
          level: 'error',
          extra: { requestId, takeId, error: err instanceof Error ? err.message : String(err) },
        });
        this.sendDataChannelReply(peerId, 'macroRecorderRecordControl', {
          requestId,
          status: 'error',
          error: err instanceof Error ? err.message : String(err),
        });
      });
    } else if (action === 'macroRecorderUploadScreenshots') {
      void manager.handleUploadScreenshots(payload as any).then((reply) => {
        this.sendDataChannelReply(peerId, 'macroRecorderUploadScreenshots', reply);
        Sentry.logger.info('[AdminPeerHandler] macro-recorder DC reply sent macroRecorderUploadScreenshots', {
          feature: 'macro-recorder', transport: 'webrtc-dc', direction: 'send',
          action, requestId, takeId, latencyMs: Date.now() - receiveTs,
        });
      }).catch((err) => {
        Sentry.captureMessage('[MACRO-SCREENSHOT] handleUploadScreenshots threw', {
          level: 'error',
          extra: { requestId, takeId, error: err instanceof Error ? err.message : String(err) },
        });
        this.sendDataChannelReply(peerId, 'macroRecorderUploadScreenshots', {
          requestId,
          results: [],
          error: err instanceof Error ? err.message : String(err),
        });
      });
    } else {
      Sentry.logger.info('[AdminPeerHandler] macro-recorder Unknown macroRecorder action', {
        feature: 'macro-recorder', action,
      });
    }
  }

  private sendDataChannelReply(peerId: string, action: string, message: unknown): void {
    const payload = JSON.stringify({
      header: { target: 'system', action, isReply: true, source: 'host' },
      body: { message },
    });
    this.deps.peerCoordinator.sendToPeer(peerId, payload);
  }

  /**
   * Send a fire-and-forget notification to the admin peer (not a reply).
   * Used to push upload status updates to admin without a preceding request.
   */
  public sendUploadStatusNotification(status: {
    takeId: string;
    status: 'started' | 'progress' | 'complete' | 'failed';
    progressPercent?: number;
    error?: string;
    videoKey?: string;
  }): void {
    if (!this.isAdminSession || !this.adminPeerId) return;

    const payload = JSON.stringify({
      header: { target: 'system', action: 'macroRecorderUploadStatus', isReply: false, source: 'host' },
      body: { message: status },
    });
    this.deps.peerCoordinator.sendToPeer(this.adminPeerId, payload);
    Sentry.logger.info('[AdminPeerHandler] macro-recorder upload status notification sent', {
      feature: 'macro-recorder', transport: 'webrtc-dc', direction: 'send',
      takeId: status.takeId, uploadStatus: status.status,
    });
  }

  // ========================================
  // On-demand elevated process launch
  // ========================================

  private static readonly ALLOWED_EXECUTABLES: ReadonlySet<string> = new Set([
    'C:\\Windows\\System32\\WindowsPowerShell\\v1.0\\powershell.exe',
    'C:\\Windows\\System32\\cmd.exe',
  ]);

  private handleLaunchElevatedProcess(msg: Record<string, unknown>): void {
    const path = typeof msg.path === 'string' ? msg.path : '';
    const args = typeof msg.arguments === 'string' ? msg.arguments : '';
    const cwd = typeof msg.workingDirectory === 'string' ? msg.workingDirectory : 'C:\\';

    if (!AdminPeerHandler.ALLOWED_EXECUTABLES.has(path)) {
      console.error('[AdminPeerHandler] Rejected elevated launch — path not in allowlist:', path);
      return;
    }

    const hostId = this.deps.appCoordinator.getHostAuthId();
    this.deps.ipcService.send(
      generators.system.launchElevatedProcess(hostId, path, args, cwd, undefined, { fromAdminSession: true }),
    );
    console.log('[AdminPeerHandler] On-demand elevated process launch requested:', path);
    this.resetIdleTimer();
  }

  // ========================================
  // Admin WebSocket messages (from realtime handler)
  // ========================================

  public handleAdminSessionMessage(message: { type: string; [key: string]: unknown }): void {
    if (message.type !== AdminMessageType.ADMIN_SESSION_END) return;
    if (!this.isAdminSession || !this.adminPeerId) return;

    const { reboot, exitAction } = message as { reboot?: boolean; exitAction?: AdminSessionExitAction; type: string };
    // A bare server ADMIN_SESSION_END (no exitAction) must not clobber the
    // session's non-destructive default — fall back to the current action
    // (e.g. 'restart_game' for macro-recorder sessions).
    const parsedExitAction = parseAdminSessionExitAction({ exitAction, reboot }, this.adminExitAction);
    console.log(`[AdminPeerHandler] Received ADMIN_SESSION_END from server (reboot=${!!reboot}, exitAction=${parsedExitAction})`);

    this.adminExitAction = parsedExitAction;
    if (parsedExitAction === 'reboot' || reboot) {
      this.adminRebootRequested = true;
    }

    if (this.adminDisconnectReason === 'unexpected') {
      this.adminDisconnectReason = 'admin_exit';
    }

    const kickReason = parsedExitAction === 'reboot' || reboot
      ? 'admin_session_end:reboot'
      : 'admin_session_end:server';
    this.deps.peerCoordinator.kickPeer(this.adminPeerId, kickReason);
  }

  // ========================================
  // Public getters
  // ========================================

  public getIsAdminSession(): boolean {
    return this.isAdminSession;
  }

  public getAdminPeerId(): string | null {
    return this.adminPeerId;
  }

  // ========================================
  // Idle timer
  // ========================================

  public resetIdleTimer(): void {
    if (this.isAdminSession && this.adminPeerId) {
      this.startIdleTimer();
    }
  }

  private startIdleTimer(): void {
    this.clearIdleTimer();

    this.idleWarningTimer = setTimeout(() => {
      if (!this.isAdminSession || !this.adminPeerId) return;
      console.warn('[AdminPeerHandler] Admin idle warning — 1 minute remaining');

      const remainingMs = this.config.idleTimeout - this.config.idleWarning;
      this.deps.realtimeService.sendMessage(
        websocket.generators.admin(AdminMessageType.ADMIN_SESSION_TIMEOUT_WARNING, {
          sessionId: this.adminPeerId,
          remainingSeconds: Math.round(remainingMs / 1000),
        }),
      );
    }, this.config.idleWarning);

    this.idleKickTimer = setTimeout(() => {
      if (!this.isAdminSession || !this.adminPeerId) return;
      console.warn('[AdminPeerHandler] Admin idle timeout expired — kicking admin peer');
      this.adminDisconnectReason = 'timeout';
      this.deps.peerCoordinator.kickPeer(this.adminPeerId, 'kicked:admin_idle');
    }, this.config.idleTimeout);
  }

  private clearIdleTimer(): void {
    if (this.idleWarningTimer) {
      clearTimeout(this.idleWarningTimer);
      this.idleWarningTimer = null;
    }
    if (this.idleKickTimer) {
      clearTimeout(this.idleKickTimer);
      this.idleKickTimer = null;
    }
  }
}
